import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Factory, Mail, Lock, User, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const { user, login, register, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '' });
  
  useEffect(() => {
    // Check if already authenticated
    const check = async () => {
      const isAuthenticated = await checkAuth();
      if (isAuthenticated) {
        navigate('/dashboard', { replace: true });
      }
    };
    check();
  }, [checkAuth, navigate]);
  
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const result = await login(loginData.email, loginData.password);
    
    if (result.success) {
      toast.success('Connexion réussie');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
    
    setIsLoading(false);
  };
  
  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const result = await register(registerData.email, registerData.password, registerData.name);
    
    if (result.success) {
      toast.success('Inscription réussie');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
    
    setIsLoading(false);
  };
  
  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Background Image */}
      <div className="hidden lg:flex lg:w-1/2 relative login-bg">
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-primary/30" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center">
                <Factory className="w-7 h-7" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="font-heading font-bold text-3xl tracking-tight">TAP-CN</h1>
                <p className="text-xs font-mono uppercase tracking-wider text-white/70">Industrial OS</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            <h2 className="font-heading font-bold text-4xl lg:text-5xl tracking-tight uppercase leading-tight">
              Gestion d'Atelier<br />Nouvelle Génération
            </h2>
            <p className="text-lg text-white/80 max-w-md font-body">
              Visualisez vos îlots, planifiez vos actions et optimisez votre production avec TAP-CN.
            </p>
            <div className="flex items-center gap-4 pt-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400" />
                <span className="text-sm font-mono uppercase tracking-wider">Temps réel</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-400" />
                <span className="text-sm font-mono uppercase tracking-wider">IA Intégrée</span>
              </div>
            </div>
          </div>
          
          <div className="text-sm text-white/50 font-mono">
            Powered by Taptools.fr
          </div>
        </div>
      </div>
      
      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center">
              <Factory className="w-7 h-7 text-primary-foreground" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl tracking-tight">TAP-CN</h1>
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Industrial OS</p>
            </div>
          </div>
          
          <Card className="border-2 rounded-sm shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)]">
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-heading font-bold text-2xl uppercase tracking-tight">
                Bienvenue
              </CardTitle>
              <CardDescription className="font-mono text-xs uppercase tracking-wider">
                Connectez-vous pour accéder à votre atelier
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab" className="uppercase font-medium tracking-wide text-xs">
                    Connexion
                  </TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab" className="uppercase font-medium tracking-wide text-xs">
                    Inscription
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="industrial-label">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="vous@exemple.com"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          className="pl-10 rounded-sm border-2"
                          required
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className="industrial-label">Mot de passe</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          className="pl-10 rounded-sm border-2"
                          required
                          data-testid="login-password-input"
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full btn-industrial rounded-sm h-11"
                      disabled={isLoading}
                      data-testid="login-submit-btn"
                    >
                      {isLoading ? 'Connexion...' : 'Se connecter'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </TabsContent>
                
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name" className="industrial-label">Nom complet</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="Jean Dupont"
                          value={registerData.name}
                          onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                          className="pl-10 rounded-sm border-2"
                          required
                          data-testid="register-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email" className="industrial-label">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="vous@exemple.com"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          className="pl-10 rounded-sm border-2"
                          required
                          data-testid="register-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password" className="industrial-label">Mot de passe</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="register-password"
                          type="password"
                          placeholder="••••••••"
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          className="pl-10 rounded-sm border-2"
                          required
                          minLength={6}
                          data-testid="register-password-input"
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full btn-industrial rounded-sm h-11"
                      disabled={isLoading}
                      data-testid="register-submit-btn"
                    >
                      {isLoading ? 'Inscription...' : 'Créer un compte'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
